import { apiBase, apiKey, brand, locale } from './env';
import type { Problem } from './types';

export interface FetchOptions {
  /** Extra query parameters appended to the URL. Falsy values are omitted. */
  query?: Record<string, string | number | boolean | null | undefined>;
  signal?: AbortSignal;
  /** Skip the default brand + locale params. */
  raw?: boolean;
}

export class HojApiError extends Error {
  status: number;
  problem?: Problem;

  constructor(message: string, status: number, problem?: Problem) {
    super(message);
    this.name = 'HojApiError';
    this.status = status;
    this.problem = problem;
  }
}

function buildUrl(path: string, opts: FetchOptions): string {
  // apiBase can be either absolute (https://api.hofj.com) or relative
  // (/__api/hofj when using the Vite dev proxy). Resolve relative ones
  // against window.location.origin so URL() always gets an absolute base.
  const isAbsoluteBase = /^https?:\/\//i.test(apiBase);
  const base = isAbsoluteBase
    ? `${apiBase.replace(/\/$/, '')}/`
    : `${window.location.origin}${apiBase.startsWith('/') ? '' : '/'}${apiBase.replace(/\/$/, '')}/`;

  const url = new URL(path.replace(/^\//, ''), base);
  if (!opts.raw) {
    if (brand) url.searchParams.set('brand', brand);
    if (locale) url.searchParams.set('locale', locale);
  }
  if (opts.query) {
    for (const [k, v] of Object.entries(opts.query)) {
      if (v === undefined || v === null || v === '') continue;
      url.searchParams.set(k, String(v));
    }
  }
  return url.toString();
}

export async function fetchHoj<T>(
  path: string,
  opts: FetchOptions = {},
): Promise<T> {
  const url = buildUrl(path, opts);
  let response: Response;
  try {
    response = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${apiKey}`,
        Accept: 'application/json',
      },
      signal: opts.signal,
    });
  } catch (err) {
    throw new HojApiError(
      err instanceof Error ? err.message : 'Network error',
      0,
    );
  }

  if (!response.ok) {
    let problem: Problem | undefined;
    try {
      problem = (await response.json()) as Problem;
    } catch {
      // body is not json
    }
    throw new HojApiError(
      problem?.detail ?? problem?.title ?? `HTTP ${response.status}`,
      response.status,
      problem,
    );
  }

  return (await response.json()) as T;
}
